import { USER_ROLES, normalizeRole } from './roles.js'

/** Bump when orientation or surface copy changes materially. */
export const ONBOARDING_VERSION = 'guide-v1'

const ONBOARDING_ROLE_KEYS = new Set(['account_manager', 'client', 'influencer'])

/**
 * @param {string | undefined} role
 * @param {string | null | undefined} tenantId
 * @returns {'account_manager' | 'client' | 'influencer' | null}
 */
export function toOnboardingRoleKey(role, tenantId = undefined) {
  const canonical = normalizeRole(role, tenantId)
  if (canonical === USER_ROLES.ACCOUNT_MANAGER) return 'account_manager'
  if (canonical === USER_ROLES.CLIENT) return 'client'
  if (canonical === USER_ROLES.INFLUENCER) return 'influencer'
  return null
}

export function createEmptyGuidesState() {
  return { version: ONBOARDING_VERSION, orientations: {}, surfacesSeen: [] }
}

/**
 * @param {unknown} raw
 * @returns {{ version: string, orientations: Record<string, { complete?: boolean, snoozedUntil?: string }>, surfacesSeen: string[] }}
 */
export function normalizeGuidesState(raw) {
  if (!raw || typeof raw !== 'object') return createEmptyGuidesState()
  const obj = /** @type {Record<string, unknown>} */ (raw)
  if (obj.version !== ONBOARDING_VERSION) return createEmptyGuidesState()

  const orientations = {}
  if (obj.orientations && typeof obj.orientations === 'object' && !Array.isArray(obj.orientations)) {
    for (const [key, val] of Object.entries(obj.orientations)) {
      if (!ONBOARDING_ROLE_KEYS.has(key) || !val || typeof val !== 'object' || Array.isArray(val)) continue
      const row = /** @type {Record<string, unknown>} */ (val)
      const entry = {}
      if (row.complete === true) entry.complete = true
      if (typeof row.snoozedUntil === 'string' && row.snoozedUntil.trim()) {
        entry.snoozedUntil = row.snoozedUntil.trim()
      }
      if (Object.keys(entry).length) orientations[key] = entry
    }
  }

  const surfacesSeen = Array.isArray(obj.surfacesSeen)
    ? [...new Set(obj.surfacesSeen.filter((s) => typeof s === 'string' && s.trim()).map((s) => s.trim()))]
    : []

  return { version: ONBOARDING_VERSION, orientations, surfacesSeen }
}

/**
 * @param {unknown} state
 * @param {unknown} patch
 * @param {{ role?: string, tenantId?: string | null }} [actor]
 */
const GUIDE_ACTIONS = new Set([
  'mark_orientation_complete',
  'snooze_orientation',
  'mark_surface_seen',
])

export function mergeGuidesPatch(state, patch, actor = {}) {
  const next = normalizeGuidesState(state)
  if (!patch || typeof patch !== 'object') return next
  const body = /** @type {Record<string, unknown>} */ (patch)
  const action = typeof body.action === 'string' ? body.action.trim() : ''
  if (!action) {
    const err = new Error('action is required')
    err.statusCode = 400
    throw err
  }
  if (!GUIDE_ACTIONS.has(action)) {
    const err = new Error('Unknown onboarding guides action')
    err.statusCode = 400
    throw err
  }

  const roleKey = toOnboardingRoleKey(
    typeof body.role === 'string' ? body.role : actor.role,
    body.tenant_id !== undefined ? body.tenant_id : actor.tenantId,
  )

  if (action === 'mark_orientation_complete') {
    if (!roleKey) {
      const err = new Error('A valid role is required for orientation updates')
      err.statusCode = 400
      throw err
    }
    next.orientations[roleKey] = { complete: true }
  }

  if (action === 'snooze_orientation') {
    if (!roleKey) {
      const err = new Error('A valid role is required for orientation updates')
      err.statusCode = 400
      throw err
    }
    const daysRaw = Number(body.days)
    const days = Number.isFinite(daysRaw) && daysRaw > 0 ? daysRaw : 7
    const until = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString()
    next.orientations[roleKey] = { snoozedUntil: until }
  }

  if (action === 'mark_surface_seen') {
    const surfaceId = typeof body.surface_id === 'string' ? body.surface_id.trim() : ''
    if (!surfaceId) {
      const err = new Error('surface_id is required')
      err.statusCode = 400
      throw err
    }
    if (!next.surfacesSeen.includes(surfaceId)) next.surfacesSeen.push(surfaceId)
  }

  return next
}

/** @param {unknown} state */
export function toApiGuidesState(state) {
  const normalized = normalizeGuidesState(state)
  return {
    version: normalized.version,
    orientations: normalized.orientations,
    surfaces_seen: normalized.surfacesSeen,
  }
}

/** @param {unknown} api */
export function fromApiGuidesState(api) {
  if (!api || typeof api !== 'object') return createEmptyGuidesState()
  const row = /** @type {Record<string, unknown>} */ (api)
  return normalizeGuidesState({
    version: row.version,
    orientations: row.orientations,
    surfacesSeen: row.surfaces_seen ?? row.surfacesSeen,
  })
}
